import { useMemo } from "react";
import {
  buildAdminExcelOrderedRows,
  groupConfirmationDetailRows,
} from "../../utils/excelSheetDisplay";
import { formatVehicleNumberDisplay } from "../../utils/vehicleNumberUtils";
import { resolveVehicleDisplayStatus } from "../../utils/vehicleDisplayStatus";

function displayValue(label, value) {
  if (value == null || value === "") return "—";
  let text = String(value);
  if (text.trim() === "—" || text.trim() === "-") return "—";
  const nk = String(label).toLowerCase();
  if (
    text === "0" &&
    (nk.includes("engine") || nk.includes("chasis") || nk.includes("chassis"))
  ) {
    return "—";
  }
  if ((nk === "emi" || nk === "pos") && /^0+(\.0+)?$/.test(text.replace(/,/g, ""))) {
    return "—";
  }
  if (nk.includes("registration") || nk.includes("vehicle no")) {
    const formatted = formatVehicleNumberDisplay(text);
    return formatted !== "—" ? formatted : text;
  }
  return text;
}

function renderCard(row, index) {
  return (
    <div
      className={`fv-detail-item${
        String(row.label).toLowerCase().includes("address") ||
        String(row.label).toLowerCase().includes("loaded")
          ? " fv-detail-item--full"
          : ""
      }`}
      key={`${row.label}-${index}`}
    >
      <span className="fv-detail-label">{row.label}</span>
      <span
        className={
          String(row.label).trim().toLowerCase() === "status"
            ? `fv-detail-value fv-detail-status-value fv-detail-status-value--${String(
                displayValue(row.label, row.value)
              )
                .toLowerCase()
                .replace(/\s+/g, "-")}`
            : "fv-detail-value"
        }
      >
        {displayValue(row.label, row.value)}
      </span>
    </div>
  );
}

/**
 * All uploaded Excel columns (repo admin).
 * layout="cards" — Find Vehicles style grid (label on top, value below).
 * layout="list" — confirmation list rows with colon separator.
 */
export default function AdminExcelFieldGrid({
  caseData,
  loading = false,
  enriching = false,
  layout = "cards",
  className = "",
  omitLabels = null,
  groupSections = false,
  emptyMessage = "No Excel details available for this vehicle.",
}) {
  const rows = useMemo(() => {
    if (!caseData) return [];
    const all = buildAdminExcelOrderedRows(caseData, { showEmpty: true });
    const filtered =
      !omitLabels || omitLabels.size === 0
        ? all
        : all.filter((row) => !omitLabels.has(row.label));
    const alreadyHasStatus = filtered.some(
      (row) => String(row.label).trim().toLowerCase() === "status"
    );
    if (alreadyHasStatus) return filtered;
    const statusRow = {
      label: "Status",
      value: resolveVehicleDisplayStatus(caseData),
    };
    const instalmentIndex = filtered.findIndex(
      (row) => String(row.label).trim().toLowerCase() === "instalment"
    );
    if (instalmentIndex >= 0) {
      const next = filtered.slice();
      next.splice(instalmentIndex + 1, 0, statusRow);
      return next;
    }
    return [statusRow, ...filtered];
  }, [caseData, omitLabels]);

  if (loading || enriching) {
    return <p className="cf-excel-loading">Loading full Excel sheet…</p>;
  }

  if (!rows.length) {
    return <p className="cf-excel-loading">{emptyMessage}</p>;
  }

  if (layout === "cards") {
    if (groupSections) {
      const sections = groupConfirmationDetailRows(rows);
      return (
        <div className={`fv-detail-sections${className ? ` ${className}` : ""}`}>
          {sections.map((section) => (
            <section key={section.title} className="fv-detail-section">
              <h3 className="fv-detail-section__title">{section.title}</h3>
              <div className="fv-detail-grid">
                {section.rows.map((row, index) => renderCard(row, index))}
              </div>
            </section>
          ))}
        </div>
      );
    }
    return (
      <div className={`fv-detail-grid${className ? ` ${className}` : ""}`}>
        {rows.map((row, index) => renderCard(row, index))}
      </div>
    );
  }

  return (
    <div className={className || undefined}>
      <div className="lrms-trace-list" role="list">
        {rows.map((row, index) => (
          <div
            key={`${row.label}-${index}`}
            className="lrms-trace-row"
            role="listitem"
          >
            <span className="lrms-trace-row__label">{row.label}</span>
            <span className="lrms-trace-row__sep">:</span>
            <span className="lrms-trace-row__value">
              {displayValue(row.label, row.value)}
            </span>
          </div>
        ))}
      </div>

      <div className="company-table-wrap cf-excel-sheet cf-excel-sheet--desktop">
        <table className="users-table excel-grid-table cf-excel-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Value</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={`${row.label}-${index}`}>
                <td className="cf-excel-field">{row.label}</td>
                <td className="cf-excel-value">{displayValue(row.label, row.value)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
